/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the application.
 * Uses JWT tokens with auto-refresh for session persistence.
 */

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {
  login,
  googleLogin,
  verifyTwoFactor,
  register,
  logout as apiLogout,
  getCurrentUser,
  isAuthenticated as checkIsAuthenticated,
  startAutoRefresh,
  stopAutoRefresh,
  getStoredUser,
  clearTokens,
  getAccessToken as getAuthAccessToken,
} from '@business-assistant/shared/api/auth';
import type { User, GoogleLoginCredentials } from '@business-assistant/shared/api/auth';

// =============================================================================
// TYPES
// =============================================================================

export interface AuthContextType {
  /** Current authenticated user (null if not logged in) */
  user: Omit<User, 'passwordHash' | 'twoFactorSecret'> | null;
  /** Session token (access token) */
  sessionToken: string | null;
  /** Loading state during auth operations */
  isLoading: boolean;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Login with email/password function */
  login: (emailOrUsername: string, password: string, rememberMe?: boolean) => Promise<LoginResult>;
  /** Login with Google OAuth */
  loginWithGoogle: (credentials: GoogleLoginCredentials) => Promise<LoginResult>;
  /** Verify 2FA function */
  verify2FA: (code: string) => Promise<LoginResult>;
  /** Register function */
  register: (data: RegisterData) => Promise<RegisterResult>;
  /** Logout function */
  logout: () => Promise<void>;
  /** Refresh user data */
  refreshUser: () => Promise<void>;
}

export interface LoginResult {
  success: boolean;
  requiresTwoFactor?: boolean;
  error?: string;
}

export interface RegisterResult {
  success: boolean;
  requiresEmailVerification?: boolean;
  error?: string;
}

export interface RegisterData {
  email: string;
  username: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  companyName?: string;
}

// =============================================================================
// CONTEXT
// =============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// =============================================================================
// PROVIDER
// =============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<Omit<User, 'passwordHash' | 'twoFactorSecret'> | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tempSessionToken, setTempSessionToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Load session from stored tokens on mount
  useEffect(() => {
    const loadSession = async () => {
      try {
        // Check if user has stored tokens
        if (checkIsAuthenticated()) {
          const storedUser = getStoredUser();
          const accessToken = getAuthAccessToken();

          if (storedUser && accessToken) {
            setUser({
              id: storedUser.id,
              email: storedUser.email,
              displayName: storedUser.displayName,
              firstName: storedUser.firstName,
              lastName: storedUser.lastName,
              role: storedUser.role,
              avatarUrl: storedUser.avatarUrl,
              isActive: true,
              emailVerified: true,
              twoFactorEnabled: false,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });
            setSessionToken(accessToken);
            setIsAuthenticated(true);

            // Start auto-refresh of tokens
            startAutoRefresh();
          } else {
            // Try to get current user from server (this may refresh tokens)
            const currentUser = await getCurrentUser();
            if (currentUser) {
              setUser(currentUser);
              setSessionToken(getAuthAccessToken());
              setIsAuthenticated(true);
              startAutoRefresh();
            } else {
              // Session expired or invalid
              clearTokens();
              setIsAuthenticated(false);
            }
          }
        }
      } catch (error) {
        console.error('Failed to load session:', error);
        clearTokens();
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadSession();

    // Cleanup on unmount
    return () => {
      stopAutoRefresh();
    };
  }, []);

  // Login handler
  const handleLogin = useCallback(async (
    emailOrUsername: string,
    password: string,
    rememberMe?: boolean
  ): Promise<LoginResult> => {
    // console.log('[AuthContext] handleLogin called with:', emailOrUsername);
    try {
      // console.log('[AuthContext] Calling login API...');
      const response = await login({
        emailOrUsername,
        password,
        rememberMe,
      });
      // console.log('[AuthContext] Login API response:', response);

      if (!response.success) {
        // console.log('[AuthContext] Login failed:', response.error);
        return { success: false, error: response.error };
      }

      if (response.requiresTwoFactor) {
        // console.log('[AuthContext] 2FA required');
        // Store temporary session token for 2FA
        setTempSessionToken(response.sessionToken || null);
        return { success: true, requiresTwoFactor: true };
      }

      // Login successful
      // console.log('[AuthContext] Login successful, setting state...', { hasSessionToken: !!response.sessionToken, hasUser: !!response.user });
      if (response.sessionToken && response.user) {
        setSessionToken(response.sessionToken);
        setUser(response.user);
        setIsAuthenticated(true);
        // console.log('[AuthContext] State updated, starting auto-refresh');

        // Start auto-refresh
        startAutoRefresh();
      }

      // console.log('[AuthContext] Returning success');
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'An error occurred during login' };
    }
  }, []);

  // Google login handler
  const handleGoogleLogin = useCallback(async (
    credentials: GoogleLoginCredentials
  ): Promise<LoginResult> => {
    try {
      const response = await googleLogin(credentials);

      if (!response.success) {
        return { success: false, error: response.error };
      }

      // Login successful
      if (response.sessionToken && response.user) {
        setSessionToken(response.sessionToken);
        setUser(response.user);
        setIsAuthenticated(true);

        // Start auto-refresh
        startAutoRefresh();
      }

      return { success: true };
    } catch (error) {
      console.error('Google login error:', error);
      return { success: false, error: 'An error occurred during Google login' };
    }
  }, []);

  // Verify 2FA handler
  const handleVerify2FA = useCallback(async (code: string): Promise<LoginResult> => {
    if (!tempSessionToken) {
      return { success: false, error: 'Invalid session' };
    }

    try {
      const response = await verifyTwoFactor({
        sessionToken: tempSessionToken,
        code,
      });

      if (!response.success) {
        return { success: false, error: response.error };
      }

      // 2FA verification successful
      if (response.sessionToken && response.user) {
        setSessionToken(response.sessionToken);
        setUser(response.user);
        setTempSessionToken(null);
        setIsAuthenticated(true);

        // Start auto-refresh
        startAutoRefresh();
      }

      return { success: true };
    } catch (error) {
      console.error('2FA verification error:', error);
      return { success: false, error: 'An error occurred during verification' };
    }
  }, [tempSessionToken]);

  // Register handler
  const handleRegister = useCallback(async (data: RegisterData): Promise<RegisterResult> => {
    try {
      const response = await register(data);

      if (!response.success) {
        return { success: false, error: response.error };
      }

      return {
        success: true,
        requiresEmailVerification: response.requiresEmailVerification,
      };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'An error occurred during registration' };
    }
  }, []);

  // Logout handler
  const handleLogout = useCallback(async () => {
    // Stop auto-refresh
    stopAutoRefresh();

    try {
      await apiLogout(sessionToken || undefined);
    } catch (error) {
      console.error('Logout error:', error);
    }

    setUser(null);
    setSessionToken(null);
    setTempSessionToken(null);
    setIsAuthenticated(false);
  }, [sessionToken]);

  // Refresh user data from server (bypasses localStorage cache)
  const refreshUser = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      // Force refresh from server to get updated data (e.g., after avatar change)
      const currentUser = await getCurrentUser(undefined, true);

      if (currentUser) {
        setUser(currentUser);
        setSessionToken(getAuthAccessToken());
      } else {
        // Don't logout on failed refresh - keep using stored user data
        // This prevents logout on temporary network issues or server errors
        console.warn('[AuthContext] Failed to refresh user data, keeping current session');
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
      // Don't logout on error - the user is still authenticated
      // Only logout if token is explicitly invalid (401)
    }
  }, [isAuthenticated]);

  const value: AuthContextType = {
    user,
    sessionToken,
    isLoading,
    isAuthenticated,
    login: handleLogin,
    loginWithGoogle: handleGoogleLogin,
    verify2FA: handleVerify2FA,
    register: handleRegister,
    logout: handleLogout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// =============================================================================
// HOOK
// =============================================================================

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
